/**
 * Shared error handling for both API dialects this backend speaks.
 *
 * The older controllers (Structure, Management, Enrollment) are RPC-style: they wrap everything in
 * ScadsysCore's `TypedResponse<T>` and return **HTTP 200 even on failure**, with `success: false`
 * inside the body. A naive `.value` read renders a silently empty table instead of an error, which
 * is exactly the trap `ManagementList.tsx` used to document. `unwrap()` turns that into a throw.
 *
 * Everything newer (Activities, Configuration, Grading, Procedures, Calculations, Marks, Reports,
 * Audit) is REST-ish: bare payloads, real status codes, RFC 7807 ProblemDetails. `describeError`
 * handles both, plus the 422 case where the body is the result model rather than ProblemDetails.
 */

// ----------------------------------------------------------------- envelopes

export interface ErrorItem {
    message: string;
    error?: string;
}

export interface ErrorCollection {
    errors?: ErrorItem[];
    hasErrors?: boolean;
}

/**
 * Deliberately every-field-optional, and `errors` deliberately loose.
 *
 * These have to accept the NSwag-generated `DefaultResponse` / `TypedResponseOfX` classes, where
 * every property is optional and `errors` is the generated ErrorCollection class. Declaring
 * `success` as required here would make the guard functions reject exactly the values they exist to
 * guard - and a missing `success` is a failure anyway, which is what `unwrap` already assumes.
 */
export interface DefaultResponse {
    success?: boolean;
    message?: string | undefined;
    errors?: ErrorCollection | any;
}

export interface TypedResponse<T> extends DefaultResponse {
    value?: T;
}

/** Raised by `unwrap()` when an RPC-style endpoint answers 200 with `success: false`. */
export class ApiEnvelopeError extends Error {
    readonly items: ErrorItem[];

    constructor(message: string, items: ErrorItem[]) {
        super(message);
        this.name = "ApiEnvelopeError";
        this.items = items;
    }
}

function envelopeMessage(response: DefaultResponse): string {
    const items = response?.errors?.errors ?? [];
    const joined = items
        .map((e) => e?.message || e?.error)
        .filter(Boolean)
        .join(" ");
    return response?.message || joined || "The request failed.";
}

/**
 * Unwrap a `TypedResponse<T>` from the RPC-style controllers, throwing when `success` is false.
 * Use for every Structure / Management / Enrollment call.
 */
export function unwrap<T>(response: TypedResponse<T>): T | undefined {
    if (!response || response.success !== true) {
        throw new ApiEnvelopeError(envelopeMessage(response), response?.errors?.errors ?? []);
    }
    return response.value;
}

/** The same guard for `DefaultResponse` endpoints (create / update / delete) that carry no value. */
export function unwrapVoid(response: DefaultResponse): void {
    if (!response || response.success !== true) {
        throw new ApiEnvelopeError(envelopeMessage(response), response?.errors?.errors ?? []);
    }
}

// ------------------------------------------------------------------ findings

export type FindingSeverity = "Information" | "Warning" | "Error";

/**
 * Loose on purpose, for the same reason as the envelope types above: this has to accept the
 * NSwag-generated `CalculationFinding` class, where every property is optional.
 */
export interface CalculationFinding {
    code?: string;
    /** The backend serializes this plain C# enum as a number; `severityOf` turns it into a name. */
    severity?: FindingSeverity | number;
    message?: string;
    ruleID?: string;
    targetEntityType?: string;
    targetEntityID?: string;
    assignmentID?: string;
    qualificationModuleID?: string;
    studentID?: string;
    procedureVersionID?: string;
    markResultID?: string;
    parameters?: Record<string, unknown>;
}

const SEVERITY_NAMES: FindingSeverity[] = ["Information", "Warning", "Error"];

export function severityOf(finding: CalculationFinding): FindingSeverity {
    if (typeof finding?.severity === "number") return SEVERITY_NAMES[finding.severity] ?? "Information";
    return finding?.severity ?? "Information";
}

/** A 422 from the calculation endpoints carries the result model, not ProblemDetails. */
export function findingsFromError(error: any): CalculationFinding[] {
    const data = isApiException(error) ? error.result ?? parseJson(error.response) : error?.response?.data;
    if (Array.isArray(data?.findings)) return data.findings;
    if (Array.isArray(data?.preview?.findings)) return data.preview.findings;
    return [];
}

/** HTTP status of a failure, whichever client threw it. Used to spot the Playwright 503. */
export function statusOf(error: any): number | undefined {
    if (isApiException(error)) return error.status;
    return error?.response?.status;
}

// --------------------------------------------------------------- description

function parseJson(text?: string): any {
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch {
        return undefined;
    }
}

/**
 * The generated client does not reject with an axios error - it throws its own `ApiException`,
 * where the parsed body is on `result` and the raw text on `response` (a string, not an axios
 * response object). Reading only `error.response.data` therefore produced "An unexpected server
 * error occurred." for every failure that came through a generated call, discarding the
 * ProblemDetails the server actually sent.
 */
function isApiException(error: any): boolean {
    return !!error && typeof error.status === "number" && typeof error.response === "string";
}

export function describeError(error: any): string {
    if (error instanceof ApiEnvelopeError) return error.message;

    if (isApiException(error)) {
        const body = error.result ?? parseJson(error.response);
        return (
            body?.detail ??
            body?.title ??
            (typeof body === "string" && body ? body : undefined) ??
            (error.response && error.response.length < 400 ? error.response : undefined) ??
            error.message ??
            "The request failed."
        );
    }

    const data = error?.response?.data;

    // RPC-style envelope surfaced through a non-200 (rare, but cheap to handle).
    if (data && typeof data === "object" && data.success === false) {
        return envelopeMessage(data as DefaultResponse);
    }

    return (
        data?.detail ??
        data?.title ??
        (typeof data === "string" && data ? data : undefined) ??
        error?.message ??
        "The request failed."
    );
}

/**
 * `describeError`, but appending any calculation findings the body carried. Use on the
 * procedure / calculation screens where a 422 explains itself through findings.
 */
export function describeErrorWithFindings(error: any): string {
    const findings = findingsFromError(error);
    const base = describeError(error);
    if (!findings.length) return base;
    const detail = findings
        .filter((f) => severityOf(f) === "Error")
        .map((f) => f.message)
        .join(" ");
    return detail ? `${base} ${detail}` : base;
}
